import { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface User {
  id: string;
  email: string;
  name: string;
  firstName: string;
  lastName: string;
  userType: string;
  agency?: string;
  agencyAffiliation?: string;
  iataNumber?: string;
  isAdminSession?: boolean;
  adminReturnUrl?: string | null;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  salesforceToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (token: string, user: User, salesforceToken?: string) => void;
  logout: () => void;
  loginAsUser: (input: string) => Promise<void>;
  salesforceAdminLogin: (token: string, contactId: string, timestamp: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Export for apiClient to read tokens from same storage
export const TOKEN_KEY = 'fhxd_token';
export const SALESFORCE_TOKEN_KEY = 'fhxd_salesforce_token';
export const USER_KEY = 'fhxd_user';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [salesforceToken, setSalesforceToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const persistAuth = (newToken: string, newUser: User, newSalesforceToken?: string) => {
    const sfToken = newSalesforceToken ?? 'fhxd-lead-auth';
    localStorage.setItem(TOKEN_KEY, newToken);
    localStorage.setItem(SALESFORCE_TOKEN_KEY, sfToken);
    localStorage.setItem(USER_KEY, JSON.stringify(newUser));
    setToken(newToken);
    setSalesforceToken(sfToken);
    setUser(newUser);
  };

  // Load auth state from localStorage on mount
  useEffect(() => {
    const storedToken = localStorage.getItem(TOKEN_KEY);
    const storedSalesforceToken = localStorage.getItem(SALESFORCE_TOKEN_KEY);
    const storedUser = localStorage.getItem(USER_KEY);

    if (storedToken && storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        setToken(storedToken);
        setUser(parsedUser);
        if (storedSalesforceToken) {
          setSalesforceToken(storedSalesforceToken);
        }
      } catch {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(SALESFORCE_TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
      }
    }
    setIsLoading(false);
  }, []);

  const login = (newToken: string, newUser: User, newSalesforceToken?: string) => {
    persistAuth(newToken, newUser, newSalesforceToken);
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(SALESFORCE_TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    setToken(null);
    setSalesforceToken(null);
    setUser(null);
    window.location.href = '/';
  };

  const loginAsUser = async (input: string) => {
    if (!token) {
      throw new Error('Authentication required to use admin login');
    }

    const trimmed = input.trim();
    const isLeadId = /^00Q[\w]{12,15}$/i.test(trimmed);
    const isContactId = /^003[\w]{12,15}$/.test(trimmed);
    const requestBody =
      isLeadId || isContactId ? { contactId: trimmed } : { experienceUrl: input };

    const response = await fetch('/api/fhxd-auth/login-as-user', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(requestBody),
    });

    const data = await response.json();

    if (!data.success) {
      throw new Error(data.message || 'Login as user failed');
    }

    const nextUser: User = {
      ...data.data.user,
      adminReturnUrl: data.data.adminReturnUrl,
    };
    persistAuth(data.data.token, nextUser);
  };

  const salesforceAdminLogin = async (
    adminToken: string,
    contactId: string,
    timestamp: string
  ) => {
    const response = await fetch('/api/fhxd-auth/salesforce-admin-login', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ token: adminToken, contactId, timestamp }),
    });

    const data = await response.json();

    if (!data.success) {
      throw new Error(data.message || 'Salesforce admin login failed');
    }

    persistAuth(data.data.token, data.data.user);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        salesforceToken,
        isAuthenticated: !!token && !!user,
        isLoading,
        login,
        logout,
        loginAsUser,
        salesforceAdminLogin,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
